import { type FC, useEffect, useImperativeHandle, useRef } from 'react'
import { useLanguage } from '../../hooks/useLanguage.js'
import { useQuery } from '@based'

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

const loadTurnstileScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve()

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${SCRIPT_SRC}"]`,
  )
  if (existing) {
    if (existing.dataset.loaded) return Promise.resolve()
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Turnstile script')),
        { once: true },
      )
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))
    document.head.appendChild(script)
  })
}

export type TurnstileHandle = {
  reset: () => void
}

export const Turnstile = ({
  ref,
  setTurnstileToken,
}: {
  ref?: React.Ref<TurnstileHandle>
  setTurnstileToken: (token: string | null) => void
}) => {
  const [lang] = useLanguage()
  const {
    data: { turnstileSitekey },
  } = useQuery('payment-stripe-pubkey')
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | undefined>(undefined)

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current)
      }
    },
  }))

  useEffect(() => {
    let disposed = false

    ;(async () => {
      await loadTurnstileScript()
      if (disposed) return

      const container = containerRef.current
      if (!container) throw new Error('Turnstile container ref not attached')

      if (window.turnstile) {
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: turnstileSitekey,
          language: lang.code,
          theme: 'dark',
          callback: setTurnstileToken,
          'expired-callback': () => setTurnstileToken(null),
          'timeout-callback': () => setTurnstileToken(null),
        })
      } else {
        throw new Error('Turnstile global object is not available')
      }
    })().catch((e: unknown) => {
      if (!disposed) console.error(e)
    })

    return () => {
      disposed = true
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = undefined
      }
    }
  }, [lang.code])

  return <div ref={containerRef} />
}

Turnstile.displayName = 'Turnstile'
